(() => {
  'use strict';

  if (window.SolenaSavedItems) return;

  const WISHLIST_KEY = 'solena:wishlist:v1';
  const RECENT_KEY = 'solena:recently-viewed:v1';
  const WISHLIST_LIMIT = 100;
  const RECENT_LIMIT = 20;
  const memoryStore = new Map();
  const root = window.Shopify?.routes?.root || '/';

  const canUseLocalStorage = (() => {
    try {
      const key = 'solena:storage-test';
      window.localStorage.setItem(key, key);
      window.localStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  })();
  let storageProvider = canUseLocalStorage ? 'local' : 'memory';

  const storage = {
    get(key) {
      try {
        return storageProvider === 'local' ? window.localStorage.getItem(key) : memoryStore.get(key) || null;
      } catch (error) {
        storageProvider = 'memory';
        return memoryStore.get(key) || null;
      }
    },
    set(key, value) {
      try {
        if (storageProvider === 'local') window.localStorage.setItem(key, value);
        else memoryStore.set(key, value);
      } catch (error) {
        storageProvider = 'memory';
        memoryStore.set(key, value);
      }
    },
    remove(key) {
      try {
        if (storageProvider === 'local') window.localStorage.removeItem(key);
        memoryStore.delete(key);
      } catch (error) {
        storageProvider = 'memory';
        memoryStore.delete(key);
      }
    },
  };

  const asString = (value) => (value === null || value === undefined ? '' : String(value));
  const asMoney = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.round(number) : 0;
  };
  const normalizeItem = (item = {}) => ({
    productId: asString(item.productId || item.id),
    handle: asString(item.handle).trim(),
    title: asString(item.title).trim(),
    vendor: asString(item.vendor).trim(),
    url: asString(item.url).trim(),
    image: asString(item.image).trim(),
    imageAlt: asString(item.imageAlt || item.title).trim(),
    price: asMoney(item.price),
    compareAtPrice: asMoney(item.compareAtPrice),
    currency: asString(item.currency || window.Shopify?.currency?.active || 'USD'),
    variantId: asString(item.variantId),
    variantTitle: asString(item.variantTitle).trim(),
    available: item.available !== false,
    addedAt: Number(item.addedAt || item.savedAt) || Date.now(),
    viewedAt: Number(item.viewedAt) || Date.now(),
  });

  const readList = (key) => {
    try {
      const value = JSON.parse(storage.get(key) || '[]');
      if (!Array.isArray(value)) return [];
      return value.map(normalizeItem).filter((item) => item.productId && item.handle);
    } catch (error) {
      storage.remove(key);
      return [];
    }
  };

  const dispatchChange = (type, items) => {
    window.dispatchEvent(new CustomEvent(`solena:${type}-change`, { detail: { items, count: items.length } }));
  };
  const writeList = (key, type, items) => {
    storage.set(key, JSON.stringify(items));
    dispatchChange(type, items);
    return items;
  };

  const api = {
    get storageProvider() { return storageProvider; },
    getWishlist: () => readList(WISHLIST_KEY).slice(0, WISHLIST_LIMIT),
    isWishlisted(productId) {
      const id = asString(productId);
      return readList(WISHLIST_KEY).some((item) => item.productId === id);
    },
    addWishlist(product) {
      const item = normalizeItem(product);
      if (!item.productId || !item.handle) return readList(WISHLIST_KEY);
      const items = readList(WISHLIST_KEY).filter((entry) => entry.productId !== item.productId && entry.handle !== item.handle);
      item.addedAt = Date.now();
      items.unshift(item);
      return writeList(WISHLIST_KEY, 'wishlist', items.slice(0, WISHLIST_LIMIT));
    },
    removeWishlist(productId) {
      const id = asString(productId);
      return writeList(WISHLIST_KEY, 'wishlist', readList(WISHLIST_KEY).filter((item) => item.productId !== id));
    },
    clearWishlist() {
      storage.remove(WISHLIST_KEY);
      dispatchChange('wishlist', []);
      return [];
    },
    getRecentlyViewed: () => readList(RECENT_KEY).sort((a, b) => b.viewedAt - a.viewedAt).slice(0, RECENT_LIMIT),
    trackRecentlyViewed(product) {
      const item = normalizeItem(product);
      if (!item.productId || !item.handle) return readList(RECENT_KEY);
      const items = readList(RECENT_KEY).filter((entry) => entry.productId !== item.productId && entry.handle !== item.handle);
      item.viewedAt = Date.now();
      items.unshift(item);
      return writeList(RECENT_KEY, 'recently-viewed', items.slice(0, RECENT_LIMIT));
    },
    clearRecentlyViewed() {
      storage.remove(RECENT_KEY);
      dispatchChange('recently-viewed', []);
      return [];
    },
    subscribe(type, callback) {
      if (typeof type === 'function') {
        const listener = type;
        const offWishlist = api.subscribe('wishlist', listener);
        const offRecent = api.subscribe('recently-viewed', listener);
        return () => { offWishlist(); offRecent(); };
      }
      const eventName = `solena:${type}-change`;
      const handler = (event) => callback(event.detail || {});
      window.addEventListener(eventName, handler);
      return () => window.removeEventListener(eventName, handler);
    },
  };
  api.addWishlistItem = api.addWishlist.bind(api);
  api.removeWishlistItem = api.removeWishlist.bind(api);
  api.hasWishlistItem = api.isWishlisted.bind(api);
  api.toggleWishlistItem = (item) => {
    const normalized = normalizeItem(item);
    const existing = api.getWishlist().find((entry) => entry.productId === normalized.productId);
    if (!existing || existing.variantId !== normalized.variantId) return api.addWishlist(normalized);
    return api.removeWishlist(normalized.productId);
  };
  window.SolenaSavedItems = api;

  window.addEventListener('storage', (event) => {
    if (event.key === WISHLIST_KEY) dispatchChange('wishlist', api.getWishlist());
    if (event.key === RECENT_KEY) dispatchChange('recently-viewed', api.getRecentlyViewed());
    if (event.key === null) {
      dispatchChange('wishlist', api.getWishlist());
      dispatchChange('recently-viewed', api.getRecentlyViewed());
    }
  });

  class ProductResolver {
    constructor(maxConcurrent = 4) {
      this.cache = new Map();
      this.queue = [];
      this.active = 0;
      this.maxConcurrent = maxConcurrent;
    }

    get(handle) {
      const safeHandle = asString(handle).trim();
      if (!safeHandle) return Promise.reject(new Error('Missing product handle'));
      if (this.cache.has(safeHandle)) return this.cache.get(safeHandle);
      const request = new Promise((resolve, reject) => {
        this.queue.push({ handle: safeHandle, resolve, reject });
        this.drain();
      });
      this.cache.set(safeHandle, request);
      return request;
    }

    drain() {
      while (this.active < this.maxConcurrent && this.queue.length) {
        const job = this.queue.shift();
        this.active += 1;
        this.fetchProduct(job.handle)
          .then(job.resolve, job.reject)
          .finally(() => {
            this.active -= 1;
            this.drain();
          });
      }
    }

    async fetchProduct(handle) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!response.ok) {
          const error = new Error(`Product request failed (${response.status})`);
          error.status = response.status;
          throw error;
        }
        const product = await response.json();
        if (!product || !product.id || !Array.isArray(product.variants)) throw new Error('Invalid product response');
        return product;
      } finally {
        window.clearTimeout(timeout);
      }
    }
  }
  window.SolenaProductResolver = new ProductResolver(4);

  const analytics = (eventName, item, source) => {
    const detail = {
      product_id: item?.productId || '',
      variant_id: item?.variantId || '',
      product_handle: item?.handle || '',
      source: source || 'unknown',
      wishlist_count: api.getWishlist().length,
    };
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: eventName, ...detail });
  };

  const toast = (() => {
    let element;
    let timer;
    return (message) => {
      if (!element) {
        element = document.createElement('div');
        element.className = 'solena-saved-toast';
        element.setAttribute('role', 'status');
        element.setAttribute('aria-live', 'polite');
        document.body.append(element);
      }
      element.textContent = message;
      element.classList.add('is-visible');
      window.clearTimeout(timer);
      timer = window.setTimeout(() => element.classList.remove('is-visible'), 2600);
    };
  })();

  const parseSnapshot = (script) => {
    try {
      return normalizeItem(JSON.parse(script?.textContent || '{}'));
    } catch (error) {
      return null;
    }
  };
  const findSnapshot = (button) => {
    const id = asString(button.dataset.productId);
    const scope = button.closest('[data-solena-product-scope], [data-lumi-product-page], .lumi-product-card, product-card') || document;
    const scripts = Array.from(scope.querySelectorAll('[data-solena-product-snapshot]'));
    return parseSnapshot(scripts.find((script) => !id || script.dataset.productId === id) || scripts[0]);
  };

  const syncWishlistUi = () => {
    const items = api.getWishlist();
    const ids = new Set(items.map((item) => item.productId));
    document.querySelectorAll('[data-solena-wishlist-toggle]').forEach((button) => {
      const active = ids.has(asString(button.dataset.productId));
      const title = findSnapshot(button)?.title || 'this product';
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${title} ${active ? 'from' : 'to'} wishlist`);
      const label = button.querySelector('span');
      if (label) label.textContent = active ? 'Saved to Wishlist' : 'Save to Wishlist';
    });
    document.querySelectorAll('[data-solena-wishlist-count]').forEach((count) => {
      const value = items.length > 99 ? '99+' : String(items.length);
      count.textContent = value;
      count.hidden = items.length === 0;
      count.setAttribute('aria-label', `${items.length} saved ${items.length === 1 ? 'item' : 'items'}`);
    });
  };

  const imageSource = (product, variant) => {
    const featured = variant?.featured_image;
    if (typeof featured === 'string') return featured;
    if (featured?.src) return featured.src;
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image?.src) return product.featured_image.src;
    return Array.isArray(product.images) ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.src || '') : '';
  };
  const productUrl = (handle, variantId) => `${root}products/${encodeURIComponent(handle)}${variantId ? `?variant=${encodeURIComponent(variantId)}` : ''}`;
  const refreshSnapshot = (saved, product) => {
    const variants = product.variants || [];
    let variant = variants.find((entry) => asString(entry.id) === saved.variantId);
    let optionChanged = false;
    if (!variant && saved.variantTitle) {
      variant = variants.find((entry) => asString(entry.title).toLowerCase() === saved.variantTitle.toLowerCase());
      optionChanged = Boolean(variant);
    }
    if (!variant) {
      variant = variants.find((entry) => entry.available) || variants[0];
      optionChanged = true;
    }
    if (!variant) return { ...saved, unavailable: true, optionChanged: true };
    return {
      ...saved,
      productId: asString(product.id),
      handle: product.handle || saved.handle,
      title: product.title || saved.title,
      vendor: 'Solena Lighting',
      url: productUrl(product.handle || saved.handle, variant.id),
      image: imageSource(product, variant) || saved.image,
      imageAlt: saved.imageAlt || product.title,
      price: asMoney(variant.price),
      compareAtPrice: asMoney(variant.compare_at_price),
      variantId: asString(variant.id),
      variantTitle: variant.title || '',
      available: Boolean(variant.available),
      optionChanged,
      unavailable: false,
    };
  };

  const money = (cents, currency = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(asMoney(cents) / 100);
    } catch (error) {
      return `$${(asMoney(cents) / 100).toFixed(2)}`;
    }
  };

  const iconButton = (label, action, iconPath) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'solena-saved-card__icon-button';
    button.dataset[action] = '';
    button.setAttribute('aria-label', label);
    button.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${iconPath}</svg>`;
    return button;
  };

  const appendSnapshot = (scope, item) => {
    const script = document.createElement('script');
    script.type = 'application/json';
    script.dataset.solenaProductSnapshot = '';
    script.dataset.productId = item.productId;
    script.textContent = JSON.stringify(item);
    scope.append(script);
  };

  const createCard = (item, options = {}) => {
    const article = document.createElement('article');
    article.className = `solena-saved-card${options.refreshing ? ' is-refreshing' : ''}${item.unavailable ? ' is-unavailable' : ''}`;
    article.dataset.productId = item.productId;
    article.dataset.solenaProductScope = '';

    const visual = document.createElement('div');
    visual.className = 'solena-saved-card__visual';
    const link = document.createElement('a');
    link.href = item.url || productUrl(item.handle, item.variantId);
    link.setAttribute('aria-label', item.title || 'View product');
    if (options.recent) link.dataset.solenaRecentClick = '';
    const image = document.createElement('img');
    image.src = item.image || '';
    image.alt = item.imageAlt || item.title || '';
    image.loading = 'lazy';
    image.width = 720;
    image.height = 720;
    link.append(image);
    visual.append(link);

    if (options.showWishlist) {
      const heart = iconButton(`Add ${item.title} to wishlist`, 'solenaWishlistToggle', '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z"/>');
      heart.dataset.productId = item.productId;
      heart.dataset.source = options.source || 'recently_viewed';
      visual.append(heart);
    }

    const body = document.createElement('div');
    body.className = 'solena-saved-card__body';
    if (options.showVendor && item.vendor) {
      const vendor = document.createElement('p');
      vendor.className = 'solena-saved-card__vendor';
      vendor.textContent = item.vendor;
      body.append(vendor);
    }
    const title = document.createElement('a');
    title.className = 'solena-saved-card__title';
    title.href = link.href;
    title.textContent = item.title || 'Product';
    if (options.recent) title.dataset.solenaRecentClick = '';
    body.append(title);

    if (options.showVariant && item.variantTitle && item.variantTitle !== 'Default Title') {
      const variant = document.createElement('p');
      variant.className = 'solena-saved-card__variant';
      variant.textContent = item.variantTitle;
      body.append(variant);
    }
    if (item.optionChanged) {
      const notice = document.createElement('p');
      notice.className = 'solena-saved-card__notice';
      notice.textContent = 'Saved option is no longer available';
      body.append(notice);
    }
    if (item.unavailable) {
      const unavailable = document.createElement('p');
      unavailable.className = 'solena-saved-card__notice';
      unavailable.textContent = 'This product is no longer available.';
      body.append(unavailable);
    } else if (options.showPrice !== false) {
      const priceRow = document.createElement('p');
      priceRow.className = 'solena-saved-card__price';
      const current = document.createElement('strong');
      current.textContent = money(item.price, item.currency);
      priceRow.append(current);
      if (options.showCompare && item.compareAtPrice > item.price) {
        const compare = document.createElement('s');
        compare.textContent = money(item.compareAtPrice, item.currency);
        priceRow.append(compare);
      }
      body.append(priceRow);
    }

    if (options.wishlistPage) {
      const actions = document.createElement('div');
      actions.className = 'solena-saved-card__actions';
      const view = document.createElement('a');
      view.className = 'solena-saved-button solena-saved-button--secondary';
      view.href = link.href;
      view.textContent = 'View Product';
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'solena-saved-button';
      add.dataset.solenaWishlistAddCart = '';
      add.textContent = item.available && !item.unavailable ? 'Add to Cart' : 'Unavailable';
      add.disabled = !item.available || item.unavailable || !item.variantId;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'solena-saved-card__remove';
      remove.dataset.solenaWishlistRemove = '';
      remove.textContent = 'Remove';
      remove.setAttribute('aria-label', `Remove ${item.title} from wishlist`);
      actions.append(view, add, remove);
      body.append(actions);
    }

    article.append(visual, body);
    appendSnapshot(article, item);
    return article;
  };

  const wishlistPageState = new WeakMap();
  const setWishlistElementVisibility = (element, visible) => {
    if (!element) return;
    element.hidden = !visible;
    element.setAttribute('aria-hidden', String(!visible));
  };
  const updateWishlistPageState = (section, items = api.getWishlist()) => {
    if (!section) return [];
    const currentItems = Array.isArray(items) ? items : api.getWishlist();
    const hasItems = currentItems.length > 0;
    const empty = section.querySelector('[data-solena-wishlist-empty]');
    const grid = section.querySelector('[data-solena-wishlist-grid]');
    const toolbar = section.querySelector('[data-solena-wishlist-toolbar]');
    const note = section.querySelector('.solena-wishlist-page__device-note');
    const count = section.querySelector('[data-solena-wishlist-page-count]');
    const countLabel = section.querySelector('[data-solena-wishlist-count-label]');

    setWishlistElementVisibility(empty, !hasItems);
    setWishlistElementVisibility(grid, hasItems);
    setWishlistElementVisibility(toolbar, hasItems);
    setWishlistElementVisibility(note, hasItems);
    section.dataset.state = hasItems ? 'ready' : 'empty';
    if (count) count.textContent = String(currentItems.length);
    if (countLabel) countLabel.textContent = currentItems.length === 1 ? 'Saved Item' : 'Saved Items';
    return currentItems;
  };
  const initWishlistPage = (section) => {
    if (wishlistPageState.has(section)) return;
    const state = { token: 0, resolved: new Map() };
    wishlistPageState.set(section, state);
    renderWishlistPage(section);
    analytics('wishlist_view', null, 'wishlist_page');
  };

  const renderWishlistPage = (section) => {
    const state = wishlistPageState.get(section);
    if (!state) return;
    const token = ++state.token;
    const allItems = api.getWishlist();
    const displayLimit = Math.max(0, Number(section.dataset.displayLimit) || 0);
    const items = displayLimit ? allItems.slice(0, displayLimit) : allItems;
    const grid = section.querySelector('[data-solena-wishlist-grid]');
    const empty = section.querySelector('[data-solena-wishlist-empty]');
    const status = section.querySelector('[data-solena-wishlist-status]');
    if (!grid || !empty) return;
    updateWishlistPageState(section, allItems);
    grid.replaceChildren(...items.map((item) => createCard(item, {
      wishlistPage: true,
      refreshing: true,
      showVariant: section.dataset.showVariant !== 'false',
      showCompare: section.dataset.showCompare !== 'false',
    })));
    if (!items.length) {
      status.textContent = '';
      return;
    }
    status.textContent = 'Refreshing current product details...';
    let completed = 0;
    items.forEach((saved) => {
      window.SolenaProductResolver.get(saved.handle)
        .then((product) => refreshSnapshot(saved, product))
        .catch((error) => ({ ...saved, unavailable: error.status === 404, refreshFailed: error.status !== 404 }))
        .then((item) => {
          if (state.token !== token) return;
          const currentItems = api.getWishlist();
          updateWishlistPageState(section, currentItems);
          if (!currentItems.some((current) => current.productId === item.productId)) return;
          state.resolved.set(item.productId, item);
          const oldCard = Array.from(grid.children).find((card) => card.dataset.productId === item.productId);
          const newCard = createCard(item, {
            wishlistPage: true,
            showVariant: section.dataset.showVariant !== 'false',
            showCompare: section.dataset.showCompare !== 'false',
          });
          if (item.refreshFailed) {
            const notice = document.createElement('p');
            notice.className = 'solena-saved-card__refresh-note';
            notice.textContent = 'Current details could not be refreshed.';
            newCard.querySelector('.solena-saved-card__body')?.append(notice);
          }
          oldCard?.replaceWith(newCard);
        })
        .finally(() => {
          completed += 1;
          if (state.token === token) {
            updateWishlistPageState(section, api.getWishlist());
            if (completed === items.length) status.textContent = '';
          }
          syncWishlistUi();
        });
    });
  };

  const recentState = new WeakMap();
  const setRecentSectionState = (section, state) => {
    if (!section) return;
    const grid = section.querySelector('[data-solena-recent-grid]');
    const empty = section.querySelector('[data-solena-recent-empty]');
    const clearButton = section.querySelector('[data-solena-recent-clear]');
    const ready = state === 'ready';
    const emptyState = state === 'empty';

    setWishlistElementVisibility(grid, ready);
    setWishlistElementVisibility(empty, emptyState);
    setWishlistElementVisibility(clearButton, ready);
    section.dataset.state = state;
    section.classList.toggle('is-ready', state !== 'loading');
    section.setAttribute('aria-hidden', String(state === 'loading'));
  };

  const loadRecentSection = async (section) => {
    if (section.dataset.solenaDisabled === 'true') return;
    if (recentState.get(section) === 'loaded') return;
    recentState.set(section, 'loaded');
    setRecentSectionState(section, 'loading');
    const currentId = asString(section.dataset.currentProductId);
    const excludeCurrent = section.dataset.excludeCurrent !== 'false';
    const limit = Math.max(1, Number(section.dataset.limit) || 4);
    const uniqueRecords = new Map();
    api.getRecentlyViewed()
      .filter((item) => !excludeCurrent || item.productId !== currentId)
      .forEach((item) => {
        if (item?.handle && !uniqueRecords.has(item.handle)) uniqueRecords.set(item.handle, item);
      });
    const records = Array.from(uniqueRecords.values()).slice(0, limit);
    const grid = section.querySelector('[data-solena-recent-grid]');
    if (!records.length) {
      grid?.replaceChildren();
      setRecentSectionState(section, 'empty');
      return;
    }
    const resolved = await Promise.all(records.map((saved) => window.SolenaProductResolver.get(saved.handle).then((product) => refreshSnapshot(saved, product)).catch(() => null)));
    const items = resolved.filter((item) => item && !item.unavailable);
    if (!items.length) {
      grid?.replaceChildren();
      setRecentSectionState(section, 'empty');
      return;
    }
    grid?.replaceChildren(...items.map((item) => createCard(item, {
      recent: true,
      showPrice: section.dataset.showPrice !== 'false',
      showVendor: section.dataset.showVendor === 'true',
      showWishlist: section.dataset.showWishlist !== 'false',
      source: 'recently_viewed',
    })));
    setRecentSectionState(section, 'ready');
    syncWishlistUi();
  };

  const initRecentSection = (section) => {
    if (recentState.has(section)) return;
    if (section.dataset.solenaDisabled === 'true') {
      recentState.set(section, 'disabled');
      return;
    }
    recentState.set(section, 'waiting');
    if (!('IntersectionObserver' in window)) {
      loadRecentSection(section);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      loadRecentSection(section);
    }, { rootMargin: '300px 0px' });
    observer.observe(section);
  };

  const updateProductSnapshotForVariant = (select) => {
    const page = select.closest('[data-lumi-product-page]');
    if (!page) return;
    let variants = [];
    try { variants = JSON.parse(page.querySelector('[data-lumi-product-variants]')?.textContent || '[]'); }
    catch (error) { return; }
    const variant = variants.find((entry) => asString(entry.id) === asString(select.value));
    if (!variant) return;
    let currentSnapshot = null;
    page.querySelectorAll('[data-solena-product-snapshot]').forEach((script) => {
      const snapshot = parseSnapshot(script);
      if (!snapshot || snapshot.productId !== asString(page.dataset.productId)) return;
      snapshot.variantId = asString(variant.id);
      snapshot.variantTitle = variant.title || '';
      snapshot.price = asMoney(variant.price);
      snapshot.compareAtPrice = asMoney(variant.compare_at_price);
      snapshot.available = Boolean(variant.available);
      snapshot.image = variant.featured_image?.src || snapshot.image;
      script.textContent = JSON.stringify(snapshot);
      currentSnapshot = snapshot;
    });
    if (currentSnapshot && !window.Shopify?.designMode) api.trackRecentlyViewed(currentSnapshot);
    syncWishlistUi();
  };

  const trackCurrentProduct = (tracker) => {
    if (window.Shopify?.designMode || tracker.dataset.solenaTracked === 'true') return;
    const snapshot = parseSnapshot(tracker.querySelector('[data-solena-product-snapshot]'));
    if (snapshot) {
      tracker.dataset.solenaTracked = 'true';
      api.trackRecentlyViewed(snapshot);
    }
  };

  const openDialog = (dialog, trigger) => {
    if (!dialog) return;
    dialog._solenaTrigger = trigger || document.activeElement;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    dialog.querySelector('button, textarea')?.focus();
  };
  const closeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    const trigger = dialog._solenaTrigger;
    if (trigger && !trigger.hidden && trigger.offsetParent !== null) {
      trigger.focus();
      return;
    }
    document.querySelector('[data-solena-wishlist-empty]:not([hidden]) a[href]')?.focus();
  };

  const addToCart = async (item, button) => {
    if (!item?.variantId || item.available === false || item.unavailable) throw new Error('This option is unavailable');
    if (window.SolenaCart?.addItem) {
      await window.SolenaCart.addItem({ id: item.variantId, quantity: 1 });
      return { handledFeedback: true };
    }
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(item.variantId), quantity: 1 }] }),
    });
    if (!response.ok) throw new Error('Unable to add this item');
    const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();
    document.querySelectorAll('[data-lumi-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
    document.querySelector('[data-lumi-cart-trigger]')?.click();
    return { cart, handledFeedback: false };
  };

  const shareWishlist = async (button) => {
    const section = button.closest('[data-solena-wishlist-page]');
    const state = section ? wishlistPageState.get(section) : null;
    const items = api.getWishlist()
      .map((item) => state?.resolved.get(item.productId) || item)
      .slice(0, 20);
    if (!items.length) return;
    const links = items.map((item) => `${item.title}: ${new URL(productUrl(item.handle), window.location.origin).href}`);
    const text = `Share links to your saved lights.\n\n${links.join('\n')}\n\nWishlist tool: ${window.location.href}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'SolenaLighting Wishlist', text });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast('Wishlist links copied');
      } else {
        throw new Error('Clipboard unavailable');
      }
      analytics('wishlist_share', items[0], 'wishlist_page');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      const dialog = section?.querySelector('[data-solena-wishlist-copy-dialog]');
      const textarea = dialog?.querySelector('[data-solena-wishlist-copy-text]');
      if (textarea) textarea.value = text;
      openDialog(dialog, button);
      textarea?.select();
    }
  };

  document.addEventListener('click', async (event) => {
    const toggle = event.target.closest('[data-solena-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      const item = findSnapshot(toggle);
      if (!item) return;
      const source = toggle.dataset.source || 'product_card';
      const savedItem = api.getWishlist().find((entry) => entry.productId === item.productId);
      if (savedItem && savedItem.variantId !== item.variantId) {
        api.addWishlist(item);
        analytics('wishlist_add', item, source);
        toast('Saved option updated.');
      } else if (savedItem) {
        api.removeWishlist(item.productId);
        analytics('wishlist_remove', item, source);
        toast('Removed from your wishlist.');
      } else {
        api.addWishlist(item);
        analytics('wishlist_add', item, source);
        toast('Added to your wishlist.');
      }
      return;
    }

    const remove = event.target.closest('[data-solena-wishlist-remove]');
    if (remove) {
      const card = remove.closest('[data-product-id]');
      const item = findSnapshot(remove);
      api.removeWishlist(card?.dataset.productId || item?.productId);
      analytics('wishlist_remove', item, 'wishlist_page');
      toast('Removed from wishlist.');
      return;
    }

    const add = event.target.closest('[data-solena-wishlist-add-cart]');
    if (add) {
      const item = findSnapshot(add);
      add.disabled = true;
      add.setAttribute('aria-busy', 'true');
      const original = add.textContent;
      add.textContent = 'Adding...';
      try {
        const result = await addToCart(item, add);
        analytics('wishlist_add_to_cart', item, 'wishlist_page');
        if (!result?.handledFeedback) toast('Added to cart successfully.');
        const section = add.closest('[data-solena-wishlist-page]');
        if (section?.dataset.removeAfterAdd === 'true') api.removeWishlist(item.productId);
      } catch (error) {
        toast(error.message || 'Unable to add this item.');
      } finally {
        add.disabled = !item?.available;
        add.removeAttribute('aria-busy');
        add.textContent = original;
      }
      return;
    }

    const clear = event.target.closest('[data-solena-wishlist-clear]');
    if (clear) {
      const dialog = clear.closest('[data-solena-wishlist-page]')?.querySelector('[data-solena-wishlist-clear-dialog]');
      openDialog(dialog, clear);
      return;
    }
    const recentClear = event.target.closest('[data-solena-recent-clear]');
    if (recentClear) {
      api.clearRecentlyViewed();
      const section = recentClear.closest('[data-solena-recently-viewed]');
      section?.querySelector('[data-solena-recent-grid]')?.replaceChildren();
      setRecentSectionState(section, 'empty');
      toast('Recently viewed history cleared.');
      return;
    }
    const clearConfirm = event.target.closest('[data-solena-wishlist-clear-confirm]');
    if (clearConfirm) {
      const item = api.getWishlist()[0];
      api.clearWishlist();
      analytics('wishlist_clear', item, 'wishlist_page');
      closeDialog(clearConfirm.closest('dialog'));
      toast('Wishlist cleared.');
      return;
    }
    const share = event.target.closest('[data-solena-wishlist-share]');
    if (share) {
      await shareWishlist(share);
      return;
    }
    const cancel = event.target.closest('[data-solena-dialog-cancel]');
    if (cancel) closeDialog(cancel.closest('dialog'));

    const recentLink = event.target.closest('[data-solena-recent-click]');
    if (recentLink) analytics('recently_viewed_product_click', findSnapshot(recentLink), 'recently_viewed');
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-lumi-variant-select]')) updateProductSnapshotForVariant(event.target);
  });

  document.addEventListener('keydown', (event) => {
    const dialog = document.querySelector('.solena-saved-dialog[open]');
    if (!dialog) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog(dialog);
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(dialog.querySelectorAll('button:not([disabled]), textarea, a[href]'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const observedGrids = new WeakSet();
  const observeDynamicGrids = (scope = document) => {
    const selectors = '.lumi-product-grid, .lumi-products-row, .lumi-related-products__grid, [data-product-grid]';
    const grids = Array.from(scope.querySelectorAll?.(selectors) || []);
    if (scope.matches?.(selectors)) grids.unshift(scope);
    grids.forEach((grid) => {
      if (observedGrids.has(grid)) return;
      observedGrids.add(grid);
      const observer = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => node.nodeType === 1 && (node.matches?.('[data-solena-wishlist-toggle]') || node.querySelector?.('[data-solena-wishlist-toggle]'))))) {
          syncWishlistUi();
        }
      });
      observer.observe(grid, { childList: true, subtree: true });
    });
  };

  const init = (scope = document) => {
    syncWishlistUi();
    scope.querySelectorAll('[data-solena-wishlist-page]').forEach(initWishlistPage);
    scope.querySelectorAll('[data-solena-recently-viewed]').forEach(initRecentSection);
    scope.querySelectorAll('[data-solena-recently-viewed-tracker]').forEach(trackCurrentProduct);
    observeDynamicGrids(scope);
    if (api.storageProvider === 'memory' && !document.documentElement.dataset.solenaStorageNotice) {
      document.documentElement.dataset.solenaStorageNotice = 'true';
      toast('Saved items may not remain after you close this browser.');
    }
  };

  window.addEventListener('solena:wishlist-change', () => {
    syncWishlistUi();
    document.querySelectorAll('[data-solena-wishlist-page]').forEach(renderWishlistPage);
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
